import { Mate } from "@/types/entity";
import { MateSprite } from "@/types/game";
import { Ref, WritableComputedRef, computed, ref, watch } from "vue";
import { AssetFileTypeEnum } from "./DialogueEnum";
import { IAsset, IDialogueConfig, ISentence } from "./types";
import {
  StrategyConfig,
  useSentencesClampByRow,
  useTextClamp,
} from "./useTextClamp";

export const useDialogue = (
  configRef: WritableComputedRef<IDialogueConfig>,
  otherOptions: {
    mateListRef?: Ref<Mate[]>;
    mineMateRef?: Ref<Mate | undefined>;
    spriteListRef?: Ref<MateSprite[]>;
    dialogueBoxRef?: Ref<HTMLElement | null>;
  } = {}
) => {
  watch(
    () => otherOptions.dialogueBoxRef?.value,
    (newDialogueBoxRef) => {
      if (!newDialogueBoxRef) return;
      clampedConfigRef.trigger();
    }
  );

  const clampedConfigRef = useSentencesClampByRow(
    configRef,
    3,
    otherOptions.dialogueBoxRef ?? null
  );

  /** 记录一个sentence的名称 */
  const currentCharName = ref<string | null>(null);
  /** 记录一个sentence的avatar */
  const currentAvatar = ref<IAsset | null>(null);
  const currentBg = ref<IAsset | null>(null);
  const sentenceIndex = ref(0);
  const totalSentenceCount = computed(() => {
    return clampedConfigRef.value.sentences.length;
  });

  const mateList = computed(() => {
    return configRef.value.mateList ?? otherOptions.mateListRef?.value;
  });
  const mineMate = computed(() => {
    return configRef.value.mineMate ?? otherOptions.mineMateRef?.value;
  });
  const spriteList = computed(() => {
    return (
      configRef.value.spriteList ?? otherOptions.spriteListRef?.value ?? []
    );
  });

  const currentSentence = computed<ISentence | null>(() => {
    const sentence = clampedConfigRef.value.sentences[sentenceIndex.value];
    if (!sentence) return null;

    const { charName, content = "", assets, options = [] } = sentence;
    const currentMate = getMateFromSentence(sentence, {
      mateList: mateList.value,
      mineMate: mineMate.value,
      spriteList: spriteList.value,
    });
    const newCharName = charName ?? currentMate?.name ?? currentCharName.value;
    const newAvatar =
      assets?.avatar ?? getAvatarFromMate(currentMate) ?? currentAvatar.value;
    const newBg = assets?.bg ?? currentBg.value;
    let newPortrait: IAsset | null = null;
    if (assets?.portrait) {
      newPortrait = assets.portrait;
    } else if (currentMate) {
      newPortrait = getPortraitFromSprite(
        getSpriteByMate(currentMate, spriteList.value)
      );
    }

    return {
      ...sentence,
      optionConfig: sentence.optionConfig,
      charName: newCharName,
      content,
      options,
      assets: {
        ...assets,
        avatar: newAvatar,
        portrait: newPortrait,
        bg: newBg,
      },
    } as ISentence;
  });

  watch(currentSentence, (newSentence) => {
    if (newSentence?.charName) {
      currentCharName.value = newSentence.charName;
    }
    if (newSentence?.assets?.avatar) {
      currentAvatar.value = newSentence.assets.avatar;
    }
    if (newSentence?.assets?.bg) {
      currentBg.value = newSentence.assets.bg;
    }
  });

  const isOver = computed(() => {
    return sentenceIndex.value >= clampedConfigRef.value.sentences.length;
  });

  const next = (forceSkip: boolean = false) => {
    if (currentSentence.value?.assets?.cg?.url && !forceSkip) {
      // 视频演出时禁止通过点击跳过
      return;
    }
    if (currentSentence.value?.options?.length) return;
    sentenceIndex.value++;
  };
  /**
   * XXX: 结合ServiceWorker做资源的预加载，并且注意资源的MD5值
   * 现在暂时先改为直接下载
   */
  const preloadAssets = async ({ thresholdFilesCount = 5 }) => {
    const assets = _parseAssets(configRef.value.sentences);
    const uniqueAssets = [...new Set(assets)];
    const filesToPreload = uniqueAssets.slice(0, thresholdFilesCount);
    const res = await _downloadAssets(filesToPreload);
    return res;
  };

  const _downloadAssets = async (assets: string[]) => {
    const urls = assets.map((asset) => new URL(asset, import.meta.url));
    const res = await Promise.all(urls.map((url) => fetch(url)));
    return res;
  };

  const _parseAssets = (sentences: ISentence[]) => {
    const assets = sentences.reduce((acc: string[], sentence: ISentence) => {
      const urls = sentence.assets
        ? Object.values(sentence.assets).map((asset) => asset.url)
        : [];
      return [...acc, ...urls] as string[];
    }, [] as string[]);
    return assets;
  };

  const reset = () => {
    sentenceIndex.value = 0;
    currentCharName.value = null;
    currentAvatar.value = null;
  };

  const startNextDialogue = (nextDialogue: IDialogueConfig) => {
    reset();
    configRef.value = nextDialogue;
  };

  return {
    currentSentence,
    isOver,
    next,
    preloadAssets,
    startNextDialogue,
    totalSentenceCount,
    sentenceIndex,
  };
};

function getAvatarFromMate(mate: Mate | null): IAsset | null {
  if (!mate) return null;
  return mate.mediaUrl
    ? {
        fileType: AssetFileTypeEnum.IMAGE,
        url: mate.mediaUrl,
      }
    : null;
}

function getPortraitFromSprite(sprite: MateSprite | null): IAsset | null {
  if (!sprite) return null;
  return sprite.portraitMedia?.url
    ? {
        fileType: AssetFileTypeEnum.IMAGE,
        url: sprite.portraitMedia?.url,
      }
    : null;
}

function getSpriteByMate(
  mate: Mate,
  spriteList: MateSprite[]
): MateSprite | null {
  return spriteList.find((sprite) => sprite?.mateId === mate.id) ?? null;
}

function getMateFromSentence(
  sentence: ISentence,
  otherOptions: {
    mateList?: Mate[];
    mineMate?: Mate;
    spriteList?: MateSprite[];
  }
): Mate | null {
  const { mateId } = sentence;
  if (!mateId) return null;
  if (mateId === "mine" && otherOptions.mineMate) {
    return otherOptions.mineMate;
  } else if (typeof mateId === "function") {
    return mateId();
  } else {
    return otherOptions.mateList?.find((m) => m.id === mateId) ?? null;
  }
}

function parseConfigWithClampStrategy(
  config: IDialogueConfig,
  strategyConfig: StrategyConfig
): IDialogueConfig {
  const sentences = config.sentences.reduce(
    (acc: ISentence[], sentence: ISentence) => {
      const { content } = sentence;
      const segments = useTextClamp(content, strategyConfig);
      return [
        ...acc,
        ...segments.map((segment) => ({ ...sentence, content: segment })),
      ];
    },
    [] as ISentence[]
  );
  return {
    ...config,
    sentences,
  };
}
